import cleanCoords from "@turf/clean-coords";

const COORD_PRECISION = 6;

function roundCoord(value: number): number {
  const factor = 10 ** COORD_PRECISION;
  return Math.round(value * factor) / factor;
}

function roundPosition(position: GeoJSON.Position): GeoJSON.Position {
  const rounded: GeoJSON.Position = [roundCoord(position[0]), roundCoord(position[1])];
  if (position.length > 2) {
    for (let i = 2; i < position.length; i += 1) {
      rounded[i] = position[i];
    }
  }
  return rounded;
}

function closeRing(ring: GeoJSON.Position[]): GeoJSON.Position[] {
  if (ring.length < 3) {
    return ring;
  }

  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) {
    return ring;
  }

  return [...ring, first];
}

function isPolygonGeometry(value: unknown): value is GeoJSON.Polygon {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    (value as GeoJSON.Geometry).type === "Polygon"
  );
}

function isFinitePosition(position: unknown): position is GeoJSON.Position {
  return (
    Array.isArray(position) &&
    position.length >= 2 &&
    typeof position[0] === "number" &&
    Number.isFinite(position[0]) &&
    typeof position[1] === "number" &&
    Number.isFinite(position[1])
  );
}

/** Turf cleanCoords throws "coord is required" on null/undefined ring positions. */
function canCleanPolygonCoordinates(geometry: GeoJSON.Polygon): boolean {
  const rings = geometry.coordinates;
  if (!Array.isArray(rings) || rings.length === 0) {
    return false;
  }

  return rings.every(
    (ring) => Array.isArray(ring) && ring.length > 0 && ring.every(isFinitePosition),
  );
}

function polygonFromCleanCoordsResult(value: unknown): GeoJSON.Polygon {
  if (
    typeof value === "object" &&
    value !== null &&
    "geometry" in value &&
    isPolygonGeometry((value as GeoJSON.Feature).geometry)
  ) {
    return (value as GeoJSON.Feature<GeoJSON.Polygon>).geometry;
  }

  if (isPolygonGeometry(value)) {
    return value;
  }

  throw new Error("cleanCoords did not return a Polygon geometry");
}

function passthroughPolygon(geometry: GeoJSON.Polygon): GeoJSON.Polygon {
  return {
    type: "Polygon",
    coordinates: Array.isArray(geometry.coordinates) ? geometry.coordinates : [],
  };
}

export function normalizePolygonGeometry(geometry: GeoJSON.Polygon): GeoJSON.Polygon {
  if (!canCleanPolygonCoordinates(geometry)) {
    return passthroughPolygon(geometry);
  }

  try {
    const geom = polygonFromCleanCoordsResult(cleanCoords(geometry));

    return {
      type: "Polygon",
      coordinates: geom.coordinates.map((ring) =>
        closeRing(ring.map((position) => roundPosition(position))),
      ),
    };
  } catch {
    return passthroughPolygon(geometry);
  }
}

export function normalizeGeometryGeoJson(geometry: GeoJSON.Polygon): string {
  return JSON.stringify(normalizePolygonGeometry(geometry));
}
